import { useEffect } from "react";
import { useQueryClient } from "react-query";
import { NucToast } from "src/reusable/components/Toast";
import PartsAISplashScreen from "./components/PartsAISplashScreen";
import {
  useActiveDatasetIds,
  useChatStore,
} from "src/views/assistant/stores/ChatStore";
import { useSyncPersonaToStore } from "src/views/assistant/hooks/useSyncPersonaToStore";
import { useHistory, useLocation } from "react-router-dom";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { usePartsAIStore } from "./store/PartsAIStore";
import { useCreatePartsAIRun } from "./services/PartsAIRunService";
import { buildPartsAIChatUrl } from "./utils/chatUrl";

// When a user uploads a file before sending a message we pick it up so a pre-upload
// dialogue is reused instead of being orphaned by a fresh createDialogue call.
interface PartsAISplashLocationState {
  initialUploadDialogueId?: number;
}

// Module-level so the consuming component's effect dependency arrays don't
// churn. The splash never streams, so nothing to terminate.
const NOOP_TERMINATE_CONNECTION = () => {};

const PartsAISplashPage = () => {
  const { isLoading: isLoadingPersona } = useSyncPersonaToStore({
    allowUrlSync: false, // PartsAI doesn't use URL persona sync
    ignoreDialogueHistory: true, // always start fresh
  });

  const {
    config: chatConfig,
    setActiveDialogueId,
    setInitialUserMessage,
    clearUploadedFiles,
    setProductConfig,
    registerChatUrlBuilder,
    isChatReady,
  } = useChatStore();

  const { setPartsAIRunId, setDialogueId, reset } = usePartsAIStore();
  const history = useHistory();
  const location = useLocation<PartsAISplashLocationState>();
  const initialUploadDialogueId = location.state?.initialUploadDialogueId;

  // Get active dataset IDs from store's computed selector (matching AtomAssist pattern)
  const activeDatasetIds = useActiveDatasetIds();

  // We deliberately do NOT call ``usePartsAIChat`` here. The splash never
  // streams, all the streaming state the splash forwards to
  // ``PartsAISplashScreen`` is constant here (no message sent,
  // no connection opened), so we pass static defaults.

  const {
    mutateAsync: createDialogue,
    isLoading: isCreatingDialogue,
    error: dialogueError,
    isError: isDialogueError,
  } = useCreateDialogue();

  const { mutateAsync: createPartsAIRun, isLoading: isCreatingRun } =
    useCreatePartsAIRun();

  const handleSubmit = async (userInput: string): Promise<boolean> => {
    // Check if chat is ready - modelId and modelVersionNum can be null (backend defaults to GeneralLLM)
    if (!isChatReady()) {
      NucToast({
        message: "Chat is not ready. Please wait for configuration to load.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }

    try {
      // If ChatPromptInput.onFilesSelected created a dialogue when the user
      // uploaded a file, reuse it.
      if (initialUploadDialogueId) {
        console.log(
          "[PartsAISplashPage] Reusing pre-existing dialogue from upload:",
          initialUploadDialogueId,
        );
        setDialogueId(initialUploadDialogueId);

        const run = await createPartsAIRun({
          dialogue_id: initialUploadDialogueId,
        });
        setPartsAIRunId(run.id);
        setInitialUserMessage(userInput);

        // Clear the router state so a later return to /parts doesn't re-attach
        // to a stale dialogue id.
        history.replace({ pathname: location.pathname, state: undefined });
        history.push(`/parts/chat/${run.id}/${initialUploadDialogueId}`);
        return true;
      }

      console.log("[PartsAISplashPage] Creating dialogue with config:", {
        modelId: chatConfig?.modelId,
        modelVersionNum: chatConfig?.modelVersionNum,
        activeDatasetIds,
        topSearchResults: chatConfig?.topSearchResults,
      });

      // Create a new dialogue for this message
      const newDialogueId = await createDialogue({
        conversation: {
          model_id: chatConfig?.modelId ?? null,
          model_version_number: chatConfig?.modelVersionNum ?? null,
          search_datasets: activeDatasetIds,
          n_search_results: chatConfig?.topSearchResults || 5,
          model_inference_data: {
            infer_data: JSON.stringify([]),
            stream: true,
            streaming_response_required: true,
            text_gen_model_params: chatConfig?.chatControlMetadata,
          },
          stream: true,
          tool_calling: true, // PartsAI always has tool calling enabled
          chat_config_id: chatConfig?.chatConfigId || null,
          product: "parts-ai",
        },
      });
      console.log(
        "[PartsAISplashPage] Dialogue created with ID:",
        newDialogueId,
      );
      setDialogueId(newDialogueId);

      console.log(
        "[PartsAISplashPage] Creating Parts AI run with dialogue_id:",
        newDialogueId,
      );
      // Create a new Parts AI run with the dialogue
      const run = await createPartsAIRun({ dialogue_id: newDialogueId });
      const runId = run.id;
      console.log("[PartsAISplashPage] Parts AI run created with ID:", runId);
      setPartsAIRunId(runId);

      // Store the user message in the chat store for the chat page to retrieve
      setInitialUserMessage(userInput);

      // Navigate to chat page for the new dialogue with run ID
      history.push(`/parts/chat/${runId}/${newDialogueId}`);

      return true;
    } catch (error) {
      console.error(
        "[PartsAISplashPage] Failed to create dialogue or run:",
        error,
      );
      NucToast({
        message: "Failed to start chat. Please try again.",
        type: "error",
        hideProgressBar: false,
      });
      return false;
    }
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    // reset chat state when mounting splash page (matching AtomAssist pattern)
    setActiveDialogueId(null);
    clearUploadedFiles();
    // The composer only adds to the document scope, so scope left by a
    // previous chat (any product) is dropped here.
    useChatStore.getState().setSourceDataRecordIds([]);
    reset(); // Reset Parts AI store
    // Set product configuration for Parts AI
    setProductConfig({
      productKey: "parts-ai",
      productName: "Parts AI",
      basePath: "/parts",
    });
    registerChatUrlBuilder("parts-ai", (dialogueId, rowKind) =>
      buildPartsAIChatUrl(dialogueId, rowKind, queryClient),
    );
  }, [
    setActiveDialogueId,
    clearUploadedFiles,
    reset,
    setProductConfig,
    registerChatUrlBuilder,
    queryClient,
  ]);

  // Show loading while persona config is being loaded
  if (isLoadingPersona) {
    return null;
  }

  return (
    <PartsAISplashScreen
      chatStreamError={
        isDialogueError ? (dialogueError as Error)?.message || "" : ""
      }
      isChatConnectionError={isDialogueError}
      isChatLoading={isCreatingDialogue || isCreatingRun}
      isChatEnded={false}
      isTurnActive={false}
      terminateConnection={NOOP_TERMINATE_CONNECTION}
      sendUserMessage={handleSubmit}
      recentsKind="run"
    />
  );
};

export default PartsAISplashPage;
